import {
	BankAccountEntityType,
	OrganizationEntityType,
	PatientEntityType,
} from "@/lib/supabase/supabaseTypes";
import { getBankAccount } from "../actions/get-bank-account/get-bank-account";
import { DocumentPropsForReturnType } from "../actions/useExportActions";
import {
	resolvePaymentSlipParties,
	validatePaymentSlipCreditor,
	validatePaymentSlipDebtor,
} from "../../pdf-templates/pdf-template-components/qr-payment-slip/payment-slip-parties";
import type { FieldProblem } from "../../store/Jobs/field-problems.types";
import { getPatientDebtorProblems } from "../../store/Jobs/utils";

export interface InvoicePreflightResult {
	/** Job-page fields to flag. */
	problems: FieldProblem[];
	/** Every problem as text, including those on other screens; empty when the invoice can be issued. */
	messages: string[];
	/** Where to fix a guarantor or client debtor, which isn't editable on the job page. */
	settingsTarget?: { label: string; path: string; testId: string };
}

interface InvoicePreflightParams {
	documentProps: Pick<
		DocumentPropsForReturnType,
		"job" | "client" | "guarantor" | "patient"
	>;
	organization: OrganizationEntityType;
	bankAccountsLookup: Record<number, BankAccountEntityType>;
	/** The debtor's name and address are printed on every Rechnung. */
	validateDebtor: boolean;
	/** The bank account's details only matter when a QR slip is printed. */
	validateCreditor: boolean;
}

/**
 * Everything that would make an invoice fail after its number was already taken: the
 * bank account the payment slip resolves, and for a Rechnung the slip's debtor and
 * creditor. Runs before createInvoiceSingle.
 */
export const runInvoicePreflight = ({
	documentProps,
	organization,
	bankAccountsLookup,
	validateDebtor,
	validateCreditor,
}: InvoicePreflightParams): InvoicePreflightResult => {
	const { job, client, guarantor, patient } = documentProps;
	const { debtor, debtorKind, bankAccountOwner } = resolvePaymentSlipParties({
		job,
		client,
		guarantor,
		patient: patient as PatientEntityType | null,
	});

	const { bankAccount, error: bankAccountError } = getBankAccount({
		bankAccountsLookup,
		lab: organization,
		job,
		recipient: bankAccountOwner,
	});
	if (!bankAccount) {
		return {
			problems: [
				{ fieldId: "jobs.bank_account_id", message: bankAccountError },
			],
			messages: [bankAccountError],
		};
	}
	const problems: FieldProblem[] = [];
	const messages: string[] = [];

	const [creditorProblem] = validateCreditor
		? validatePaymentSlipCreditor(bankAccount)
		: [];
	if (creditorProblem) {
		problems.push({
			fieldId: "jobs.bank_account_id",
			message: creditorProblem,
		});
		messages.push(creditorProblem);
	}

	if (!validateDebtor) return { problems, messages };

	if (debtorKind === "patient") {
		const patientProblems = getPatientDebtorProblems(
			debtor as PatientEntityType
		);
		problems.push(...patientProblems);
		messages.push(
			...new Set(patientProblems.map((problem) => problem.message))
		);
		return { problems, messages };
	}

	const debtorProblems = validatePaymentSlipDebtor(debtor);
	if (debtorProblems.length === 0) return { problems, messages };

	messages.push(...debtorProblems.map((problem) => problem.message));
	return {
		problems,
		messages,
		settingsTarget:
			debtorKind === "guarantor"
				? {
						label: "Garanten öffnen",
						path: "/organisation/administration/garanten",
						testId: "invoice-preflight-open-guarantors",
					}
				: {
						label: "Kundeneinstellungen öffnen",
						path: `/client/${client.id}/einstellungen/allgemeine-informationen`,
						testId: "invoice-preflight-open-client-settings",
					},
	};
};
